import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { TeamMembersService } from '../../services/teamMembers/teamMembersService';
import { StoreService } from '../../services/store/applicationStateService';

interface User {
  "First Name": string | null;
  "Last Name": string | null;
  "Login Email": string | null;
  Role: string | null;
  Status: string | null;
}

const UserTable: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<User>({
    "First Name": '',
    "Last Name": '',
    "Login Email": '',
    Role: '',
    Status: '',
  });
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        //const response = await fetch('http://localhost:3000/api/getUsers');
        const userId = StoreService.getStorage("userId");
        const response = await TeamMembersService.getUsers(userId);
        if (response.status === 200) {
          const data = response.data;
          setUsers(data.users || []);
        } else {
        }
      } catch (error) {
      }
    };

    fetchUsers();
  }, []);

  const handleAddUser = async () => {
    try {
      // const response = await fetch('http://localhost:3000/api/saveUser', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(newUser),
      // });
      const response = await TeamMembersService.saveUser(newUser);

      if (response.status === 200) {
        const data = response.data;
        setUsers((prevUsers) => [...prevUsers, data.user]);
        setOpenDialog(false);
        resetForm();
        setSuccessMessage('User added successfully!');
      } else {
      }
    } catch (error) {
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    try {
      // const response = await fetch('http://localhost:3000/api/updateUser', {
      //   method: 'PUT',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(selectedUser),
      // });
      const response = await TeamMembersService.updateUser(selectedUser);

      if (response.status === 200) {
        const updatedUser = response.data;
        window.location.reload();
      } else {
      }
    } catch (error) {
    }
  };

  const handleEditIconClick = (user: User) => {
    setIsEditing(true);
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const resetForm = () => {
    setIsEditing(false);
    setSelectedUser(null);
    setNewUser({
      "First Name": '',
      "Last Name": '',
      "Login Email": '',
      Role: '',
      Status: '',
    });
  };

  const filteredUsers = users.filter((user) =>
    (user["First Name"] || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ padding: 3, maxWidth: '1200px', margin: '0 auto' }}>

      {/* Search and Add User Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          sx={{ width: 240, marginRight: 2, borderRadius: 1 }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Button
          variant="contained"
          sx={{
            height: 36,
            borderRadius: 2,
            backgroundColor: 'green',
            '&:hover': {
              backgroundColor: 'darkgreen',
            },
          }}
          onClick={() => {
            setIsEditing(false);
            setOpenDialog(true);
          }}
        >
          Add User
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: 'green', color: 'white', fontWeight: 'bold' }}>First Name</TableCell>
              <TableCell sx={{ backgroundColor: 'green', color: 'white', fontWeight: 'bold' }}>Last Name</TableCell>
              <TableCell sx={{ backgroundColor: 'green', color: 'white', fontWeight: 'bold' }}>Login Email</TableCell>
              <TableCell sx={{ backgroundColor: 'green', color: 'white', fontWeight: 'bold' }}>Role</TableCell>
              <TableCell sx={{ backgroundColor: 'green', color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ backgroundColor: 'green', color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user, index) => (
                <TableRow key={index}>
                  <TableCell>{user["First Name"] || 'N/A'}</TableCell>
                  <TableCell>{user["Last Name"] || 'N/A'}</TableCell>
                  <TableCell>{user["Login Email"] || 'N/A'}</TableCell>
                  <TableCell>{user.Role || 'N/A'}</TableCell>
                  <TableCell>{user.Status || 'N/A'}</TableCell>
                  <TableCell>
                    <IconButton color="default" sx={{ fontSize: 18 }} onClick={() => handleEditIconClick(user)}>
                      <EditIcon sx={{ color: 'black', fontSize: 18 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ fontStyle: 'italic' }}>
                  No users available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{isEditing ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          {Object.keys(newUser).map((key) => (
            <TextField
              key={key}
              label={key}
              variant="outlined"
              fullWidth
              sx={{ marginBottom: 2 }}
              value={
                isEditing
                  ? selectedUser?.[key as keyof User] || ''
                  : newUser[key as keyof User] || ''
              }
              onChange={(e) =>
                isEditing
                  ? setSelectedUser({
                      ...selectedUser!,
                      [key]: e.target.value,
                    })
                  : setNewUser({ ...newUser, [key]: e.target.value })
              }
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              color: 'red',
              borderColor: 'red',
              '&:hover': {
                backgroundColor: 'lightcoral',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={isEditing ? handleEditUser : handleAddUser}
            sx={{
              backgroundColor: 'green',
              '&:hover': {
                backgroundColor: 'darkgreen',
              },
            }}
          >
            {isEditing ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      {successMessage && (
        <Snackbar
          open={!!successMessage}
          autoHideDuration={3000}
          onClose={() => setSuccessMessage('')}
        >
          <Alert severity="success">{successMessage}</Alert>
        </Snackbar>
      )}

      {/* Footer */}
      <Box sx={{ textAlign: 'center', marginTop: 3, fontSize: '14px', color: '#777' }}>
        <footer>
          <p>&copy; 2025 Celerbots. All Rights Reserved.</p>
        </footer>
      </Box>
    </Box>
  );
};

export default UserTable;
